import React from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { IoHeart, IoClose, IoBook, IoEye, IoHeartSharp } from 'react-icons/io5';
import { estimateBookReadingTime, formatReadingTime } from '../utils/readingTime';
import './SwipeCard.css';

export default function SwipeCard({ book, onSwipeLeft, onSwipeRight, isTop }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-18, 18]);
  const likeOpacity = useTransform(x, [0, 80], [0, 1]);
  const dislikeOpacity = useTransform(x, [-80, 0], [1, 0]);
  const scale = isTop ? 1 : 0.95;

  const resetCard = () => {
    animate(x, 0, { type: 'spring', stiffness: 500, damping: 30 });
  };

  const handleDragEnd = (_, info) => {
    if (info.offset.x > 100) {
      animate(x, 500, { duration: 0.3 });
      setTimeout(() => {
        const result = onSwipeRight(book);
        // Si le callback retourne false (popup affiché), remettre la carte
        if (result === false) resetCard();
      }, 300);
    } else if (info.offset.x < -100) {
      animate(x, -500, { duration: 0.3 });
      setTimeout(() => {
        const result = onSwipeLeft(book);
        if (result === false) resetCard();
      }, 300);
    } else {
      resetCard();
    }
  };

  const triggerSwipe = (direction) => {
    animate(x, direction === 'right' ? 500 : -500, { duration: 0.3 });
    setTimeout(() => {
      if (navigator.vibrate) navigator.vibrate(50);
      let result;
      if (direction === 'right') {
        result = onSwipeRight(book);
      } else {
        result = onSwipeLeft(book);
      }
      if (result === false) resetCard();
    }, 300);
  };

  const genreLabels = {
    romance: '💕 Romance', fantasy: '🗡️ Fantasy', thriller: '🔍 Thriller',
    scifi: '🚀 Sci-Fi', drama: '🎭 Drame', horror: '👻 Horreur',
    humor: '😂 Humour', poetry: '✨ Poésie',
  };

  return (
    <motion.div
      className="swipe-card"
      style={{ x, rotate, scale, zIndex: isTop ? 10 : 5 }}
      drag={isTop ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.9}
      onDragEnd={handleDragEnd}
      initial={{ scale: isTop ? 1 : 0.95, opacity: isTop ? 1 : 0.7 }}
      animate={{ scale: isTop ? 1 : 0.95, opacity: isTop ? 1 : 0.7 }}
      transition={{ type: 'spring', stiffness: 300, damping: 25 }}
    >
      {/* Overlays */}
      <motion.div className="swipe-overlay swipe-overlay-like" style={{ opacity: likeOpacity }}>
        <IoHeartSharp />
        <span>LIKE</span>
      </motion.div>
      <motion.div className="swipe-overlay swipe-overlay-dislike" style={{ opacity: dislikeOpacity }}>
        <IoClose />
        <span>NOPE</span>
      </motion.div>

      {/* Cover */}
      <div className="swipe-card-cover" style={{ background: book.coverImage ? `url(${book.coverImage}) center/cover no-repeat` : book.coverGradient }}>
        <div className="swipe-card-cover-pattern"></div>
        <div className="swipe-card-cover-inner">
          <span className="swipe-card-genre">{genreLabels[book.genre] || book.genre}</span>
          <div className="swipe-card-title-area">
            <h2 className="swipe-card-title">{book.title}</h2>
            <p className="swipe-card-author">par {book.author}</p>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="swipe-card-info">
        <p className="swipe-card-synopsis">{book.synopsis || ''}</p>
        <div className="swipe-card-tags">
          {(book.tags || []).map(tag => (
            <span key={tag} className="swipe-card-tag">#{tag}</span>
          ))}
        </div>
        <div className="swipe-card-stats">
          <span className="swipe-card-stat"><IoHeart /> {(book.likes || 0).toLocaleString('fr-FR')}</span>
          <span className="swipe-card-stat"><IoEye /> {(book.views || 0).toLocaleString('fr-FR')}</span>
          <span className="swipe-card-stat"><IoBook /> {(book.chapters || []).length} chap.</span>
          <span className="swipe-card-stat">⏱ {formatReadingTime(estimateBookReadingTime(book.chapters || []))}</span>
        </div>
      </div>

      {/* Buttons — en dehors de la zone info */}
      {isTop && (
        <div className="swipe-card-actions">
          <motion.button
            className="swipe-btn swipe-btn-dislike"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={(e) => { e.stopPropagation(); triggerSwipe('left'); }}
          >
            <IoClose />
          </motion.button>
          <motion.button
            className="swipe-btn swipe-btn-like"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={(e) => { e.stopPropagation(); triggerSwipe('right'); }}
          >
            <IoHeart />
          </motion.button>
        </div>
      )}
    </motion.div>
  );
}